/**
 * Supabase authentication helpers for Transport Team.
 * Roles: admin | driver | passenger — stored on public.profiles.
 * Run supabase-schema.sql in your Supabase project before using the app.
 */

import { supabase } from '../supabase';
import { passwordResetRedirectUrl, parsePasswordResetScope } from './passwordReset';

/** @typedef {'admin' | 'driver' | 'passenger'} UserRole */

/** Yield so Supabase auth listeners finish before the next auth API call. */
async function yieldToAuthListeners() {
  await new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Fetch the app profile for a Supabase auth user.
 * @param {string} userId
 * @returns {Promise<object|null>}
 */
async function fetchSuperAdminFlag() {
  const { data, error } = await supabase.rpc('is_super_admin');

  if (error) {
    console.error('fetchSuperAdminFlag', error);
    return false;
  }
  return Boolean(data);
}

async function fetchOrgActive(orgId) {
  if (!orgId) return true;
  const { data, error } = await supabase
    .from('organizations')
    .select('active')
    .eq('id', orgId)
    .maybeSingle();

  if (error) {
    console.error('fetchOrgActive', error);
    return true;
  }
  return data?.active !== false;
}

async function fetchMarketplaceAdminFlag(userId) {
  const { data, error } = await supabase
    .from('marketplace_admins')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('fetchMarketplaceAdminFlag', error);
    return false;
  }
  return Boolean(data?.user_id);
}

async function fetchMarketplaceDriverApplicationStatus(userId) {
  const { data, error } = await supabase
    .from('driver_applications')
    .select('status')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('fetchMarketplaceDriverApplicationStatus', error);
    return null;
  }
  return data?.status || null;
}

/** @returns {Promise<{ profile: object|null, error: object|null }>} */
export async function fetchProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'id, org_id, role, profile_scope, full_name, email, phone, active, driver_online, approval_status, driver_license_type, driver_license_file_path, pickup_address, pickup_area, age_range',
    )
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('fetchProfile', error);
    return { profile: null, error };
  }
  if (!data) return { profile: null, error: null };

  const super_admin = await fetchSuperAdminFlag();

  const org_active =
    data.profile_scope === 'team' && data.org_id ? await fetchOrgActive(data.org_id) : true;

  const marketplace_admin =
    data.profile_scope === 'marketplace' ? await fetchMarketplaceAdminFlag(userId) : false;

  const marketplace_driver_status =
    data.profile_scope === 'marketplace' && data.role === 'driver'
      ? await fetchMarketplaceDriverApplicationStatus(userId)
      : null;

  return {
    profile: { ...data, super_admin, org_active, marketplace_admin, marketplace_driver_status },
    error: null,
  };
}

/** Team driver deactivated by admin after approval (not pending/rejected registration). */
export function isDeactivatedTeamDriver(profile) {
  return (
    profile?.role === 'driver'
    && profile.profile_scope === 'team'
    && profile.active === false
    && profile.approval_status === 'approved'
  );
}

/**
 * Sign in and ensure the account matches the selected portal role.
 * @param {string} email
 * @param {string} password
 * @param {UserRole} expectedRole
 */
export async function signInForRole(email, password, expectedRole) {
  const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  });

  if (authError) {
    return { user: null, profile: null, error: authError };
  }

  const { profile, error: profileError } = await fetchProfile(authData.user.id);

  if (profileError) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'Could not load your profile. Please try again.' },
    };
  }

  if (!profile) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'No profile found. Complete registration or contact your organisation admin.' },
    };
  }

  if (profile.profile_scope !== 'team') {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'This account uses the driver booking portal. Sign in under “Book a driver” instead.' },
    };
  }

  if (profile.org_active === false) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'This organisation has been deactivated. Contact Transport Team support.' },
    };
  }

  if (profile.role !== expectedRole) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: `This account is not a ${expectedRole}. Pick the correct portal or contact your admin.` },
    };
  }

  if (expectedRole === 'driver') {
    if (profile.approval_status === 'pending') {
      await signOutSafely();
      return {
        user: null,
        profile: null,
        error: { message: 'Your driver application is awaiting admin approval.' },
      };
    }
    if (profile.approval_status === 'rejected') {
      await signOutSafely();
      return {
        user: null,
        profile: null,
        error: { message: 'Your driver application was not approved. Contact your organisation admin.' },
      };
    }
    if (profile.active === false) {
      await signOutSafely();
      return {
        user: null,
        profile: null,
        error: { message: 'This driver account is deactivated.' },
      };
    }
  }

  return { user: authData.user, profile, error: null };
}

/**
 * Sign out the current session.
 */
export async function signOut() {
  return supabase.auth.signOut();
}

/** Sign out without blocking on in-flight auth state listeners. */
export async function signOutSafely() {
  await yieldToAuthListeners();
  return supabase.auth.signOut();
}

/**
 * Current session + profile (if any).
 */
export async function getSessionWithProfile() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    return { session: null, profile: null };
  }
  const { profile } = await fetchProfile(session.user.id);
  return { session, profile };
}

/**
 * Verify org slug + invite code via RPC (no direct table read needed).
 * @param {string} slug
 * @param {string} inviteCode
 */
export async function verifyOrgInvite(slug, inviteCode) {
  const { data, error } = await supabase.rpc('verify_org_invite', {
    p_slug: slug,
    p_code: inviteCode.trim().toUpperCase(),
  });

  if (error) {
    return { org: null, error };
  }

  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.org_id) {
    return { org: null, error: { message: 'Invalid invite code or organisation link.' } };
  }

  return {
    org: { id: row.org_id, name: row.org_name },
    error: null,
  };
}

/**
 * Register a new passenger or driver (self-service) after invite verification.
 * @param {string} email
 * @param {string} password
 * @param {string} fullName
 * @param {string} orgSlug
 * @param {string} inviteCode
 * @param {'passenger' | 'driver'} role
 * @param {{ licenseType?: string, pickupAddress?: string, pickupArea?: string, ageRange?: string, licenseFilePath?: string }} [extra]
 */
export async function signUpWithOrgProfile(email, password, fullName, orgSlug, inviteCode, role, extra = {}) {
  if (role !== 'passenger' && role !== 'driver') {
    return { user: null, error: { message: 'Registration only supports passenger or driver roles.' } };
  }

  if (!password || password.length < 8) {
    return { user: null, error: { message: 'Password must be at least 8 characters.' } };
  }

  const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
    email: email.trim(),
    password,
    options: {
      data: { full_name: fullName, org_slug: orgSlug, role },
    },
  });

  if (signUpError) {
    return { user: null, error: signUpError };
  }

  const user = signUpData.user;
  if (!user) {
    return { user: null, error: { message: 'Could not create account. Try again or contact support.' } };
  }

  // Profile insert requires an authenticated session (RLS). If email confirmation is on, session may be null.
  if (!signUpData.session) {
    return {
      user: null,
      error: {
        message:
          'No session after sign-up. In Supabase → Authentication → Providers → Email, disable "Confirm email" for development, or add a DB trigger to create profiles after confirmation.',
      },
    };
  }

  const { error: profileError } = await supabase.rpc('register_member_profile', {
    p_slug: orgSlug,
    p_invite_code: inviteCode.trim().toUpperCase(),
    p_role: role,
    p_full_name: fullName.trim(),
    p_license_type: role === 'driver' ? extra.licenseType : null,
    p_pickup_address: role === 'passenger' ? extra.pickupAddress?.trim() || null : null,
    p_pickup_area: role === 'passenger' ? extra.pickupArea?.trim() || null : null,
    p_age_range: role === 'passenger' ? extra.ageRange : null,
    p_license_file_path: role === 'driver' ? extra.licenseFilePath || null : null,
    p_phone: extra.phone?.trim() || null,
  });

  if (profileError) {
    await supabase.auth.signOut();
    return { user: null, error: profileError };
  }

  return { user, error: null };
}

/**
 * Send a password reset email (works for team and marketplace accounts).
 * @param {string} email
 * @param {'team' | 'marketplace'} [scope]
 */
export async function requestPasswordReset(email, scope = 'team') {
  const normalizedScope = parsePasswordResetScope(scope);
  const redirectTo = passwordResetRedirectUrl(normalizedScope);
  if (!redirectTo || !redirectTo.startsWith('http')) {
    return {
      error: {
        message:
          'Password reset is not configured — set REACT_APP_SITE_URL to your app URL and add it to Supabase Auth redirect URLs.',
      },
    };
  }
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo,
  });
  return { error };
}

/**
 * Set a new password while signed in from a recovery link.
 * @param {string} newPassword
 */
export async function updatePasswordFromRecovery(newPassword) {
  if (!newPassword || newPassword.length < 8) {
    return { error: { message: 'Password must be at least 8 characters.' } };
  }
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error };
}

/**
 * Subscribe to auth state changes (session refresh, sign in/out).
 * @param {(event: string, session: import('@supabase/supabase-js').Session | null) => void} callback
 */
export function onAuthStateChange(callback) {
  const { data: sub } = supabase.auth.onAuthStateChange(callback);
  return () => sub.subscription.unsubscribe();
}

/**
 * Marketplace sign-in (diaspora booking — no org).
 */
/**
 * @param {string} email
 * @param {string} password
 * @param {{ expectedRole?: 'passenger' | 'driver', requireAdmin?: boolean }} [options]
 */
export async function signInMarketplace(email, password, options = {}) {
  const { expectedRole, requireAdmin } = options;

  const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  });

  if (authError) {
    return { user: null, profile: null, error: authError };
  }

  const { profile, error: profileError } = await fetchProfile(authData.user.id);

  if (profileError) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'Could not load your profile. Please try again.' },
    };
  }

  if (!profile) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'No profile found. Register for driver booking first.' },
    };
  }

  if (profile.profile_scope !== 'marketplace') {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'This account is for organisation transport. Use the team sign-in page.' },
    };
  }

  if (requireAdmin && !profile.marketplace_admin) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: 'This account does not have marketplace admin access.' },
    };
  }

  if (expectedRole && !profile.marketplace_admin && profile.role !== expectedRole) {
    await signOutSafely();
    return {
      user: null,
      profile: null,
      error: { message: `This account is not a ${expectedRole}. Use the correct sign-in option.` },
    };
  }

  return { user: authData.user, profile, error: null };
}

/**
 * Register marketplace passenger or driver.
 */
export async function signUpMarketplace(email, password, fullName, phone, role) {
  if (role !== 'passenger' && role !== 'driver') {
    return { user: null, error: { message: 'Invalid role.' } };
  }
  if (!password || password.length < 8) {
    return { user: null, error: { message: 'Password must be at least 8 characters.' } };
  }

  const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
    email: email.trim(),
    password,
    options: { data: { full_name: fullName, role } },
  });

  if (signUpError) {
    return { user: null, error: signUpError };
  }

  const user = signUpData.user;
  if (!user) {
    return { user: null, error: { message: 'Could not create account.' } };
  }

  if (!signUpData.session) {
    return {
      user: null,
      error: {
        message:
          'Check your email to confirm your account, or disable email confirmation in Supabase for development.',
      },
    };
  }

  const { error: profileError } = await supabase.rpc('register_marketplace_profile', {
    p_role: role,
    p_full_name: fullName.trim(),
    p_phone: phone?.trim() || null,
  });

  if (profileError) {
    await supabase.auth.signOut();
    return { user: null, error: profileError };
  }

  return { user, error: null };
}

/**
 * Create organisation + first admin (user must be authenticated after signUp).
 */
export async function registerOrganization(params) {
  const { data, error } = await supabase.rpc('register_organization', {
    p_name: params.name,
    p_slug: params.slug,
    p_contact_name: params.contactName,
    p_contact_email: params.contactEmail,
    p_contact_phone: params.contactPhone,
    p_city: params.city,
    p_org_type: params.orgType,
    p_country: params.country || 'Nigeria',
    p_estimated_passengers: params.estimatedPassengers || null,
    p_use_case_notes: params.useCaseNotes || null,
  });

  if (error) {
    return { orgId: null, inviteCode: null, error };
  }

  const row = Array.isArray(data) ? data[0] : data;
  return {
    orgId: row?.org_id,
    inviteCode: row?.invite_code,
    error: null,
  };
}
